/** Short tap acknowledgement. Persistent target glow lives in TargetHaloLayer. */

import React from 'react'
import { Circle, Path } from 'react-native-svg'
import {
  createAnimatedComponent,
  Easing,
  useAnimatedProps,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import {
  selectionEmphasis,
  type SelectionEmphasisTheme,
} from '../../lib/selectionEmphasis'

const AnimatedCircle = createAnimatedComponent(Circle)
const AnimatedPath = createAnimatedComponent(Path)

const PRESS_FLASH_OPACITY = 0.35
const PRESS_FLASH_MS = 160

export interface SelectionEmphasisTarget {
  id: string
  shape?: string
  positionX: number
  positionY: number
  normalStrokeColor?: string
}

interface PressFlashProps {
  target: SelectionEmphasisTarget
  clipNS: string
  theme: SelectionEmphasisTheme
  sourceStrokeColor?: string
  isDestination?: boolean
  pressNonce: number
  markerRadius?: number
  badgeRadius: number
  outerFlashWidth: number
}

function PressFlash({ target, clipNS, theme, sourceStrokeColor, isDestination, pressNonce, badgeRadius, markerRadius, outerFlashWidth }: PressFlashProps) {
  const reducedMotion = useReducedMotion()
  const opacity = useSharedValue(0)
  const defaultAccent = selectionEmphasis({
    role: 'source',
    selectedFillMode: 'lighten',
    showRegionBonuses: false,
    theme,
    internalBorderWidth: 1,
    badgeRadius,
    reducedMotion,
  })?.accent
  const accent = isDestination ? '#ffffff' : sourceStrokeColor ?? defaultAccent

  React.useLayoutEffect(() => {
    if (!accent || reducedMotion) {
      opacity.value = 0
      return
    }
    if (isDestination) {
      // Show the accepted tap immediately, before paint: no fade-in delay.
      // The wash and outline share the same 250 ms fade-out.
      opacity.value = 0.18
      opacity.value = withTiming(0, { duration: 250, easing: Easing.out(Easing.quad) })
      return
    }
    opacity.value = PRESS_FLASH_OPACITY
    opacity.value = withTiming(0, {
      duration: PRESS_FLASH_MS,
      easing: Easing.out(Easing.quad),
    })
  }, [accent, isDestination, opacity, pressNonce, reducedMotion])

  const animatedProps = useAnimatedProps(() => ({ fillOpacity: opacity.value }))
  // Share the wash's clock so both effects disappear together at 250 ms.
  const outlineProps = useAnimatedProps(() => ({ strokeOpacity: opacity.value * 2.5 }))
  if (!accent || reducedMotion) return null

  if (target.shape) {
    return (
      <>
      {markerRadius && <AnimatedCircle cx={target.positionX} cy={target.positionY} r={markerRadius + 2}
        fill="none" stroke="#171717" strokeWidth={markerRadius * 0.2}
        animatedProps={outlineProps} pointerEvents="none" />}
      {isDestination && (
        <AnimatedPath
          testID="territory-outer-press-feedback"
          d={target.shape}
          fill="none"
          stroke="#ffffff"
          strokeWidth={outerFlashWidth}
          strokeLinejoin="round"
          animatedProps={outlineProps}
          pointerEvents="none"
        />
      )}
      <AnimatedPath
        testID="territory-press-feedback"
        d={target.shape}
        fill={accent}
        animatedProps={animatedProps}
        clipPath={`url(#${clipNS}clip-${target.id})`}
        pointerEvents="none"
      />
      </>
    )
  }
  return (
    <>
    {isDestination && (
      <AnimatedCircle
        testID="territory-outer-press-feedback"
        cx={target.positionX}
        cy={target.positionY}
        r={badgeRadius * 1.5}
        fill="none"
        stroke="#ffffff"
        strokeWidth={outerFlashWidth}
        animatedProps={outlineProps}
        pointerEvents="none"
      />
    )}
    <AnimatedCircle
      testID="territory-press-feedback"
      cx={target.positionX}
      cy={target.positionY}
      r={badgeRadius * 1.5}
      fill={accent}
      animatedProps={animatedProps}
      pointerEvents="none"
    />
    </>
  )
}

export interface SelectionEmphasisLayerProps {
  clipNS: string
  theme: SelectionEmphasisTheme
  sourceStrokeColor?: string
  isDestination?: boolean
  markerRadius?: number
  badgeRadius: number
  outerFlashWidth: number
  pressed?: SelectionEmphasisTarget
  pressNonce: number
}

export function SelectionEmphasisLayer({
  clipNS,
  theme,
  sourceStrokeColor,
  isDestination,
  badgeRadius,
  markerRadius,
  outerFlashWidth,
  pressed,
  pressNonce,
}: SelectionEmphasisLayerProps) {
  return pressed ? (
    <PressFlash target={pressed} clipNS={clipNS} theme={theme}
      sourceStrokeColor={sourceStrokeColor} isDestination={isDestination} pressNonce={pressNonce}
      badgeRadius={badgeRadius} markerRadius={markerRadius} outerFlashWidth={outerFlashWidth} />
  ) : null
}
